<template>
  <CurrentEventDataProvider
    v-slot="{ data, isOutsideEventContext, designStyles }"
  >
    <div id="app" :style="designStyles">
      <!-- Application content -->
      <transition v-if="data || isOutsideEventContext" name="fade">
        <router-view />
      </transition>

      <!-- Loader  -->

      <div v-else>
        <div class="loader">Loading...</div>
      </div>

      <notifications group="default" position="bottom center" />

      <!-- Global accessibility controls — floating FAB lives on every page -->
      <AccessibilityPanel />
      <OnScreenKeyboard />
      <TypingSuggestions />

      <!-- LinkedIn-style docked private-chat windows. Lives at the app
           root so open conversations persist across route changes and
           dock to the bottom-right on every page (desktop only). -->
      <ChatDock v-if="data || isOutsideEventContext" />

      <!-- Always-visible close affordance for the LiveChat support
           modal. The widget itself sometimes hides its own X on
           mobile, and on small screens there's no "outside" area
           left for tap-to-dismiss — this button guarantees a way
           out regardless. Renders only while `chatModalOpen`. -->
      <button
        v-if="chatModalOpen"
        type="button"
        class="chat-modal-close"
        aria-label="Close support chat"
        @click="closeSupportChat"
      >
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import store from './store';
import ApiClient from '@/client';
import '@khmyznikov/pwa-install';
import Pusher from 'pusher-js';
import {
  ensureSafariUserManifest,
  isSafariBrowser
} from '@/helpers/pwa-helpers';
import AccessibilityPanel from '@/components/AccessibilityPanel';
import OnScreenKeyboard from '@/components/OnScreenKeyboard';
import TypingSuggestions from '@/components/TypingSuggestions';
import ChatDock from '@/components/ChatDock';
import {
  startFontSizeAutoAdjust,
  stopFontSizeAutoAdjust
} from '@/services/fontSizeAutoAdjust';
import { startDwellClick, stopDwellClick } from '@/services/dwellClick';
import { startStickyKeys, stopStickyKeys } from '@/services/stickyKeys';
import { startSlowKeys, stopSlowKeys } from '@/services/slowKeys';
import {
  startAccessibleTooltips,
  stopAccessibleTooltips
} from '@/services/accessibleTooltips';
import { startScreenReader, stopScreenReader } from '@/services/screenReader';

export default {
  name: 'App',
  components: {
    AccessibilityPanel,
    OnScreenKeyboard,
    TypingSuggestions,
    ChatDock
  },
  data() {
    return {
      // Mirrors the `chat-modal-open` body class set by the
      // `togglesSupportChat` mixin. Used to show / hide the
      // dedicated floating close button so the LiveChat support
      // modal is always dismissable, even on mobile.
      chatModalOpen: false
    };
  },
  computed: {
    fontScale() {
      return this.$store.getters['Accessibility/getFontScale'];
    },
    a11yFeatures() {
      return this.$store.getters['Accessibility/getFeatures'];
    }
  },
  watch: {
    // Re-resolve participant theme on every route change so admin /
    // utility views drop back to light and participant views honour
    // the stored preference.
    $route(to) {
      this.$store.dispatch('Theme/routeChanged', to && to.name);
      // New page DOM mounts after the route change — re-apply the
      // text scale so the new content honours the user's setting.
      this.$nextTick(() => this.applyRootFontSize());
    },
    fontScale: {
      immediate: true,
      handler() {
        this.applyRootFontSize();
      }
    },
    'a11yFeatures.biggerText': {
      immediate: true,
      handler() {
        this.applyRootFontSize();
      }
    },
    // Start/stop each service-backed feature based on its flag
    'a11yFeatures.dwellClick': {
      immediate: true,
      handler(v) {
        v ? startDwellClick(store) : stopDwellClick();
      }
    },
    'a11yFeatures.stickyKeys': {
      immediate: true,
      handler(v) {
        v ? startStickyKeys() : stopStickyKeys();
      }
    },
    'a11yFeatures.slowKeys': {
      immediate: true,
      handler(v) {
        v ? startSlowKeys(store) : stopSlowKeys();
      }
    },
    'a11yFeatures.tooltipsVisible': {
      immediate: true,
      handler(v) {
        v ? startAccessibleTooltips() : stopAccessibleTooltips();
      }
    },
    'a11yFeatures.screenReader': {
      immediate: true,
      handler(v) {
        v ? startScreenReader() : stopScreenReader();
      }
    },
    a11yFeatures: {
      immediate: true,
      deep: true,
      handler(f) {
        // Toggle a body class for each active accessibility feature.
        // The rules in accessibility.scss key off these classes and
        // apply high-contrast / link-highlight / dyslexia font / etc.
        if (!f) return;
        const body = document.body;
        const classMap = {
          'a11y-contrast-high': f.contrastPlus === 'high',
          'a11y-contrast-dark': f.contrastPlus === 'dark',
          'a11y-contrast-light': f.contrastPlus === 'light',
          'a11y-saturation-low': f.saturation === 'low',
          'a11y-saturation-high': f.saturation === 'high',
          'a11y-saturation-mono': f.saturation === 'mono',
          'a11y-hide-images': f.hideImages,
          'a11y-pause-animations': f.pauseAnimations,
          'a11y-highlight-links': f.highlightLinks,
          'a11y-dyslexia-friendly': f.dyslexiaFriendly,
          'a11y-text-spacing-light': f.textSpacing === 'light',
          'a11y-text-spacing-moderate': f.textSpacing === 'moderate',
          'a11y-text-spacing-heavy': f.textSpacing === 'heavy',
          'a11y-line-height-tall': f.lineHeight === 'tall',
          'a11y-line-height-taller': f.lineHeight === 'taller',
          'a11y-text-align-left': f.textAlign === 'left',
          'a11y-text-align-center': f.textAlign === 'center',
          'a11y-text-align-right': f.textAlign === 'right',
          'a11y-text-align-justify': f.textAlign === 'justify',
          'a11y-big-cursor': f.bigCursor,
          'a11y-show-tooltips': f.tooltipsVisible,
          'a11y-keyboard-nav': f.keyboardNav
        };
        Object.entries(classMap).forEach(([cls, active]) => {
          body.classList.toggle(cls, !!active);
        });
      }
    }
  },
  methods: {
    // Apply the user's text-size preference. We deliberately DO NOT
    // touch `html { font-size }` here — doing so makes every `rem`
    // grow (paddings, gaps, line-heights, etc.), which feels like a
    // page zoom rather than a text-size change. Scaling `body`'s
    // computed pixel font-size instead means only elements that
    // INHERIT their font-size grow; structural rem/em-based spacing
    // stays anchored to the original 16 px root.
    applyRootFontSize() {
      const scale = this.$store.getters['Accessibility/getFontScale'] || 1;
      const biggerText =
        !!this.$store.getters['Accessibility/getFeatures'].biggerText;
      const effective = scale * (biggerText ? 1.2 : 1);
      document.documentElement.style.setProperty(
        '--user-font-scale',
        effective
      );
      this.scaleTextNodes(effective);
    },
    // Scales the font-size of every text-bearing element by `factor`,
    // multiplying each element's OWN original size. The platform uses
    // fixed px font sizes, so a single body/html font-size change
    // can't reach them; walking the elements is the only way to scale
    // px text. Only font-size is touched — paddings/margins stay
    // fixed, so the layout doesn't "zoom", only the text grows.
    scaleTextNodes(factor) {
      const root = document.getElementById('app');
      if (!root || typeof document.createTreeWalker !== 'function') return;
      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_ELEMENT,
        null,
        false
      );
      let node = walker.currentNode;
      while (node) {
        // Skip the accessibility panel/FAB (its own controls), icons,
        // and the video/iframe regions.
        const skip =
          node.tagName === 'I' ||
          node.tagName === 'SVG' ||
          node.tagName === 'IFRAME' ||
          (node.classList &&
            (node.classList.contains('a11y-panel') ||
              node.classList.contains('a11y-fab') ||
              node.classList.contains('fa') ||
              node.classList.contains('fas') ||
              node.classList.contains('far') ||
              node.classList.contains('fal')));
        if (!skip) {
          // Only elements that directly own visible text (not pure
          // containers) — avoids scaling layout wrappers/icons.
          let hasText = false;
          for (let i = 0; i < node.childNodes.length; i++) {
            const c = node.childNodes[i];
            if (
              c.nodeType === 3 &&
              c.textContent &&
              c.textContent.trim().length
            ) {
              hasText = true;
              break;
            }
          }
          if (hasText) {
            let base = node.getAttribute('data-base-fs');
            if (base === null) {
              const cs = window.getComputedStyle(node).fontSize;
              const px = parseFloat(cs);
              if (px && !Number.isNaN(px)) {
                base = px;
                node.setAttribute('data-base-fs', String(px));
              }
            }
            if (base !== null) {
              const px = parseFloat(base);
              if (factor === 1) {
                node.style.fontSize = ''; // restore natural size at 100%
              } else {
                node.style.fontSize = (px * factor).toFixed(2) + 'px';
              }
            }
          }
        }
        node = walker.nextNode();
      }
    },
    createUrlBlob(data) {
      const blob = new Blob([JSON.stringify(data)], {
        type: 'application/json'
      });
      return URL.createObjectURL(blob);
    },

    // Hard-close the LiveChat support modal — wired to the
    // floating ✕ button rendered in our template. The widget's
    // own internal X is sometimes hidden on mobile, so this
    // button is always reachable.
    closeSupportChat() {
      try {
        if (window.LiveChatWidget && window.LiveChatWidget.call) {
          window.LiveChatWidget.call('minimize');
        }
      } catch (e) {
        /* no-op */
      }
      this.chatModalOpen = false;
      document.body.classList.remove('chat-modal-open');
    },
    injectManifest(data) {
      var current = document.querySelector('link[rel="manifest"]');

      if (!current) {
        var link = document.createElement('link');
        link.id = 'manifest';
        link.rel = 'manifest';
        link.href = this.createUrlBlob(data);
        document.head.appendChild(link);
      }
    }
  },
  mounted() {
    // ── Boot the participant theme runtime ──────────────────
    this.$store.dispatch('Theme/routeChanged', this.$route && this.$route.name);
    this.$store.dispatch('Theme/bindOsListener');

    // ── Text-scale: re-apply to dynamically-added content ────
    // Modals, chat messages, etc. mount outside route changes. When
    // the user has a non-default text scale, re-run the scaler
    // (debounced) so new text honours it too. Inactive at 100%.
    const reapplyTextScale = () => {
      const s = this.$store.getters['Accessibility/getFontScale'] || 1;
      const big = !!this.$store.getters['Accessibility/getFeatures'].biggerText;
      if (s * (big ? 1.2 : 1) === 1) return;
      if (this._textScaleDebounce) clearTimeout(this._textScaleDebounce);
      this._textScaleDebounce = setTimeout(() => this.applyRootFontSize(), 200);
    };
    try {
      this._textScaleObserver = new MutationObserver(reapplyTextScale);
      this._textScaleObserver.observe(
        document.getElementById('app') || document.body,
        { childList: true, subtree: true }
      );
    } catch (e) {
      /* MutationObserver unavailable */
    }

    // ── Track LiveChat support widget visibility ─────────────
    // The `togglesSupportChat` mixin already toggles the
    // `chat-modal-open` body class — we mirror it here so the
    // global close button (rendered in this component's template)
    // can show / hide reactively.
    if (window.LiveChatWidget && window.LiveChatWidget.on) {
      this.liveChatVisibilityHandler = (data) => {
        if (!data) return;
        this.chatModalOpen = data.visibility === 'maximized';
      };
      window.LiveChatWidget.on(
        'visibility_changed',
        this.liveChatVisibilityHandler
      );
    }

    // Start the passive mouse-behaviour tracker that auto-enlarges
    // text when it detects readability struggle. It's a no-op when
    // the user has disabled auto-adjust in Accessibility settings.
    startFontSizeAutoAdjust(store);

    Pusher.log = (msg) => {
      console.log(msg);
    };

    // Surface every new in-app notification as a transient toast so it's
    // seen live (the bell badge alone is easy to miss). Preference gating
    // already happens in the Notifications/add action, so anything that
    // reaches this `add` mutation is something the user opted to receive.
    this._notifToastUnsub = store.subscribe((mutation) => {
      if (mutation.type === 'Notifications/add' && mutation.payload) {
        const n = mutation.payload;
        // Silent entries (push feature off) are recorded in the bell list only —
        // no toast popup.
        if (n.silent) return;
        try {
          this.$notify({
            group: 'default',
            title: n.title || 'Notification',
            text: n.message || '',
            duration: 5000
          });
        } catch (e) {
          /* vue-notification unavailable — bell still updates */
        }
      }
    });

    store.subscribe((mutation) => {
      if (mutation.type.startsWith('Terms/setShown')) {
        // Safari and all iOS browsers (e.g. Chrome) use the user-specific
        // manifest, since the installed app can't see this tab's session
        if (isSafariBrowser()) {
          ensureSafariUserManifest();
        } else {
          ApiClient.getManifest().then((response) => {
            this.injectManifest(response.data);
          });
        }
      }
    });
  },
  beforeDestroy() {
    stopFontSizeAutoAdjust();
    stopDwellClick();
    stopStickyKeys();
    stopSlowKeys();
    stopAccessibleTooltips();
    stopScreenReader();
    if (this._notifToastUnsub) this._notifToastUnsub();
  }
};
</script>

<style lang="scss" scoped>
#app {
  background-color: var(--background-color);
  color: var(--color-text);
}

.loader {
  display: flex;
  height: 100vh;
  width: 100vw;
  justify-content: center;
  align-items: center;
  font-size: 28px;
  color: #888;
}
</style>

<!-- Global rules — non-scoped so they reach the bare document
     body, page wrappers, and Vue's BEM-named scrollable lists
     across pages. The bottom-nav floats fixed on mobile; we need
     to leave clearance both at the document level (page scroll)
     and inside any internally-scrollable region (the LiveStream
     side card, the session list, modals, etc.) so their last
     items aren't hidden behind it. -->
<style lang="scss">
@media (max-width: 640px) {
  body {
    padding-bottom: 80px;
  }

  .ls-side,
  .ls-side__main,
  .ls-chat-list,
  .ls-people-list,
  .ls-sponsors-list,
  .ls-speakers-list,
  .ls-sessions-list,
  .ls-info-body,
  .ls-docs-list,
  .ls-panel,
  .session-grid,
  .session-modal,
  .prog-sessions {
    padding-bottom: 80px !important;
  }
}

// ── Help & Support modal — LiveChatInc iframe constrained to a
//    centered modal panel instead of the default fullscreen on
//    mobile. The body class `chat-modal-open` is toggled by the
//    `togglesSupportChat` mixin's visibility_changed listener.
body.chat-modal-open {
  // Backdrop behind the chat widget so the rest of the page reads
  // as dimmed and the modal feels properly layered. `pointer-
  // events: auto` lets the backdrop catch tap-outside; the actual
  // close behaviour is wired in `togglesSupportChat`.
  &::before {
    content: '';
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(2px);
    z-index: 2147483640;
    pointer-events: auto;
    cursor: pointer;
  }
}

// Constrain the LiveChat iframe / container so it renders as a
// modal rectangle in the center of the viewport — works against
// the variants LiveChatInc has used over time (`chat-widget-
// container`, `livechat-` prefixes, iframe titles).
#chat-widget-container,
iframe#chat-widget,
iframe[id^='livechat-'],
iframe[id*='chat-widget'],
iframe[title*='LiveChat' i],
iframe[title*='Live chat' i] {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  right: auto !important;
  bottom: auto !important;
  transform: translate(-50%, -50%) !important;
  width: min(92vw, 420px) !important;
  height: min(80vh, 640px) !important;
  max-width: 420px !important;
  max-height: 640px !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.32), 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  z-index: 2147483641 !important;
}

@media (max-width: 480px) {
  #chat-widget-container,
  iframe#chat-widget,
  iframe[id^='livechat-'],
  iframe[id*='chat-widget'],
  iframe[title*='LiveChat' i],
  iframe[title*='Live chat' i] {
    width: 92vw !important;
    height: 78vh !important;
    border-radius: 14px !important;
  }
}

// ── Always-visible LiveChat close button ──────────────────────
// Floats top-right of the viewport while the chat modal is open,
// stacked above the iframe and backdrop so the user can always
// dismiss the widget — the LiveChat UI sometimes hides its own
// X on mobile and there's no "outside" area to tap when it
// covers the screen.
.chat-modal-close {
  position: fixed;
  top: 14px;
  right: 14px;
  z-index: 2147483646; // above iframe (…41) and backdrop (…40)
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%;
  border: none;
  background: #ffffff;
  color: #111827;
  font-size: 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22), 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: background 0.15s, transform 0.15s, box-shadow 0.15s;

  &:hover,
  &:focus-visible {
    background: #f3f4f6;
    transform: scale(1.05);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28), 0 2px 6px rgba(0, 0, 0, 0.12);
    outline: none;
  }

  &:active {
    transform: scale(0.96);
  }
}
</style>
